import React, { Fragment, useState } from 'react';
import { IncentiveIssues, dhis_states } from '../../api/getAbhaMetricsDashboard';
import { RupeeIcon2, crossIcon, infoIconNew, tickIcon } from '../common/Icons';
import { all_states, dhis_steps, getNextIncentiveState } from '../abha/util';
import moment from 'moment';
import { AnimatePresence } from 'framer-motion';
import NeedHelp from './NeedHelp';
import { AntTooltip } from '../common/AntTooltip';
import ABHAStepsModal from './ABHAStepsModal';
import { track } from '../../tracking';
import { Steps } from 'antd';
import IncentiveSteps from './IncentiveSteps';

const getContents = (
    nextMonth: string,
    currMonth: string,
    month?: string,
    incentive?: number,
    isCycleTwo?: boolean,
) => {
    return {
        'claim-incentive': {
            title: isCycleTwo
                ? `You are now delayed in claiming your DHIS incentive of ₹${(
                      incentive ?? 0
                  ).toLocaleString('en-IN')} for ${
                      month ?? 0
                  }! Claim now to receive it by next month.`
                : `Your DHIS incentive of ₹${(incentive ?? 0).toLocaleString('en-IN')} for ${
                      month ?? 0
                  } is ready. Claim before 5th ${currMonth} to receive incentive by end of ${currMonth}!`,
            desc: isCycleTwo
                ? `Any claims after 5th ${currMonth} will be processed in the next month by ABDM.`
                : `Any claims after 5th ${currMonth} will be processed in the next month and you'll receive incentives end of ${nextMonth}.`,
            cta: {
                primary: {
                    text: 'Claim now',
                },
                secondary: {
                    text: 'Mark as claimed',
                    icon: tickIcon('w-14 h-14 fill-darwin-accent-bright-blue-800'),
                },
            },
            className: isCycleTwo
                ? 'bg-darwin-accent-bright-yellow-200 border border-darwin-accent-bright-yellow-800'
                : 'bg-darwin-green-bg-light border border-darwin-green-primary bg-darwin-green-bg-light',
        },
        'confirm-incentive': {
            title: `Have you received the DHIS incentives for the month of ${month ?? 0}?`,
            desc: `Any claims after 5th ${currMonth} will be processed in the next month by ABDM.`,
            cta: {
                primary: {
                    text: 'Received',
                    icon: tickIcon('w-14 h-14 fill-white'),
                },
                secondary: {
                    text: 'Not recieved',
                    icon: crossIcon('w-12 h-12 fill-darwin-accent-bright-blue-800'),
                },
            },
            className: 'bg-white border border-darwin-neutral_new-200',
        },
        'incentive-recieved': {
            title: `You've marked DHIS incentives for the month of ${month ?? 0} as Received!`,
            className: 'bg-white border border-darwin-neutral_new-200',
        },
        'incentive-not-recieved': {
            title: `You've marked DHIS incentives for the month of ${month ?? 0} as Not Received.`,
            desc:
                'Thanks for informing us about your incentives. We will followup with NHA on this.',
            cta: {
                primary: {
                    text: 'Mark as Received',
                    icon: tickIcon('w-14 h-14 fill-white'),
                },
            },
            className: 'bg-white border border-darwin-neutral_new-200',
        },
    };
};

const IncentiveCard: React.FC<{
    onStatusChange: (
        result: {
            status?: all_states;
            month?: string;
            amount?: number;
            issue?: IncentiveIssues;
        },
        skipMonth?: boolean,
        onSuccess?: () => void,
        onFailure?: () => void,
    ) => void;
    incentive: number | undefined;
    selectedMonth: string | undefined;
    incentiveDateMonth: {
        status?: all_states | undefined;
        month?: string | undefined;
        amount?: number | undefined;
        issue?: IncentiveIssues | undefined;
        dhisstatus?: dhis_states | undefined;
        dhisissues?: IncentiveIssues | undefined;
    };
    dhisStatus?: dhis_states;
}> = ({ onStatusChange, incentive, selectedMonth, incentiveDateMonth, dhisStatus }) => {
    const status = incentiveDateMonth.status!;

    const today = moment();
    const nextMonth = today.clone().add(1, 'month').format('MMM');
    const currMonth = today.format('MMM');
    const selectedMonthDate = new Date(moment(selectedMonth, 'MMMM, YYYY').toISOString());
    const incentiveMonth = moment(selectedMonthDate).subtract(1, 'month').format('MMM, YYYY');

    const [needHelpDialogVisible, setNeedHelpDialogVisible] = useState(false);
    const [claimIncentiveDialogVisible, setClaimIncentiveDialogVisible] = useState(false);

    const primaryCta = () => {
        if (
            status === 'confirm-incentive' ||
            status === 'incentive-not-recieved' ||
            status === 'incentive-recieved'
        ) {
            track('CLAIM_STATUS_UPDATE', {
                status: 'incentive-recieved',
                month: incentiveDateMonth?.month ?? selectedMonth,
                amount: String(incentiveDateMonth?.amount),
                prevStatus: status,
            });

            onStatusChange({
                status: 'incentive-recieved',
                month: incentiveDateMonth?.month,
                amount: incentiveDateMonth?.amount,
                issue: incentiveDateMonth.issue
                    ? {
                          ...incentiveDateMonth.issue,
                          isResolved: true,
                      }
                    : undefined,
            });
        } else {
            track('CLAIM_INCENTIVES_STEPS', {
                status: 'incentive-recieved',
                month: incentiveDateMonth?.month ?? selectedMonth,
                amount: String(incentiveDateMonth?.amount),
                prevStatus: status,
            });

            setClaimIncentiveDialogVisible(true);
        }
    };

    const secondaryCta = (onSuccess?: () => void, onFailure?: () => void) => {
        let finalStatus: all_states;

        if (status === 'confirm-incentive') {
            finalStatus = 'incentive-not-recieved';
        } else {
            finalStatus = getNextIncentiveState(status) ?? 'dhis-registration';
        }

        track('CLAIM_STATUS_UPDATE', {
            status: finalStatus,
            month: incentiveDateMonth?.month ?? selectedMonth,
            amount: String(incentiveDateMonth?.amount),
            prevStatus: status,
        });

        onStatusChange(
            {
                status: finalStatus,
                month: incentiveDateMonth?.month,
                amount: incentiveDateMonth?.amount,
                issue: incentiveDateMonth.issue
                    ? {
                          ...incentiveDateMonth.issue,
                          isResolved: true,
                      }
                    : undefined,
            },
            undefined,
            onSuccess,
            onFailure,
        );
    };

    const todayDate = today.date();
    const isCycleTwo = todayDate > 5;

    const hasError = incentiveDateMonth.issue?.id && !incentiveDateMonth.issue.isResolved;

    const contents: any = getContents(nextMonth, currMonth, incentiveMonth, incentive, isCycleTwo);

    return status === 'dhis-registration' || status === 'dhis-approval' ? null : (
        <Fragment>
            <div
                className={`w-full bg-white flex flex-col rounded-8`}
                // style={{ boxShadow: '#d0cbcb 0px 4px 4px 0px' }}
            >
                <div className="rounded-t-8 gap-16 px-16 py-8 w-full flex items-center justify-between bg-white border-b border-darwin-neutral_new-200">
                    <IncentiveSteps currMonthStatus={incentiveDateMonth} dhisStatus={dhisStatus} />
                    <div className="flex flex-shrink-0 ml-auto justify-end self-start items-center gap-8">
                        <div
                            onClick={() => setNeedHelpDialogVisible(true)}
                            className="flex gap-4 text-13 bg-white cursor-pointer px-8 py-4 font-700 text-darwin-accent-bright-blue-800 rounded-4 justify-center items-center hover:bg-darwin-accent-bright-blue-100"
                        >
                            Need help?
                        </div>
                        {hasError && (
                            <AntTooltip
                                placement="bottom"
                                content={incentiveDateMonth.issue?.remark ?? 'Error'}
                            >
                                <div className="flex gap-4 cursor-pointer select-none text-12 bg-red-primary px-8 py-4 font-700 text-white rounded-4 justify-center items-center">
                                    {incentiveDateMonth.issue?.id === 'crn_error'
                                        ? 'CRN Error'
                                        : incentiveDateMonth.issue?.id === 'pfms_error'
                                        ? 'PFMS Error'
                                        : 'Error'}
                                </div>
                            </AntTooltip>
                        )}
                        <AntTooltip
                            content={
                                <div className="flex flex-col gap-8">
                                    <div className="flex flex-col gap-4">
                                        <p className="text-14">Cycle 1:</p>
                                        <p className="text-12">
                                            Claim incentive between 1 to 5th of month to recieve
                                            before the end of each month
                                        </p>
                                    </div>
                                    <div className="flex flex-col gap-4">
                                        <p className="text-14">Cycle 2:</p>
                                        <p className="text-12">
                                            Incentives claimed after 5th of month will be recieved next month
                                        </p>
                                    </div>
                                </div>
                            }
                            placement="bottomRight"
                        >
                            <div
                                style={{ background: '#E8E8E8' }}
                                className="flex gap-4 select-none text-12 px-8 py-4 font-700 text-white rounded-4 justify-center items-center"
                            >
                                {infoIconNew('fill-darwin-neutral-1000 w-14 h-14')}
                                {isCycleTwo ? (
                                    <p className="text-13 text-darwin-neutral-1000 font-400">
                                        Claim cycle 2
                                    </p>
                                ) : (
                                    <p className="text-13 text-darwin-neutral-1000 font-400">
                                        Claim cycle 1:{' '}
                                        <span className="font-700">{6 - todayDate} days left</span>
                                    </p>
                                )}
                            </div>
                        </AntTooltip>
                    </div>
                </div>
                <div className="flex gap-12 w-full rounded-8 items-center p-16">
                    <div className="flex justify-between w-full">
                        <div className="flex gap-8 flex-col">
                            <p className="dhis-title font-700 text-16">{contents[status].title}</p>
                            {contents[status].desc && (
                                <p className="dhis-desc text-13">{contents[status].desc}</p>
                            )}
                        </div>
                        {contents[status].cta && (
                            <div className="flex gap-16 items-center">
                                {contents?.[status]?.cta?.primary && (
                                    <div
                                        onClick={primaryCta}
                                        className="flex gap-4 text-14 cursor-pointer text-white px-8 py-4 font-700 bg-darwin-accent-bright-blue-800 rounded-4 justify-center items-center hover:opacity-80"
                                    >
                                        {contents[status].cta.primary.text}{' '}
                                        <span>{contents[status].cta.primary.icon}</span>
                                    </div>
                                )}
                                {contents[status].cta.secondary && (
                                    <div
                                        onClick={() => secondaryCta()}
                                        className="flex gap-4 text-14 bg-white cursor-pointer px-8 py-4 font-700 border border-darwin-accent-bright-blue-800 text-darwin-accent-bright-blue-800 rounded-4 justify-center items-center hover:bg-darwin-accent-bright-blue-100"
                                    >
                                        {contents[status].cta.secondary.text}{' '}
                                        <span>{contents[status].cta.secondary.icon}</span>
                                    </div>
                                )}
                            </div>
                        )}
                        {status === 'incentive-recieved' && hasError && (
                            <div
                                onClick={primaryCta}
                                style={{ width: 'fit-content' }}
                                className="flex gap-4 text-14 cursor-pointer text-white px-8 py-4 font-700 bg-darwin-accent-bright-blue-800 rounded-4 justify-center items-center hover:opacity-80"
                            >
                                Mark as recieved
                                <span>{tickIcon('w-14 h-14 fill-white')}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <AnimatePresence>
                {needHelpDialogVisible && (
                    <NeedHelp
                        close={() => setNeedHelpDialogVisible(false)}
                        onSubmit={onStatusChange}
                        currMonthStatus={incentiveDateMonth}
                        dhisStatus={dhisStatus}
                    />
                )}
            </AnimatePresence>
            <AnimatePresence>
                {claimIncentiveDialogVisible && (
                    <ABHAStepsModal
                        close={() => setClaimIncentiveDialogVisible(false)}
                        claimModal
                        onMark={secondaryCta}
                        markText={contents[status].cta.secondary.text}
                    />
                )}
            </AnimatePresence>
        </Fragment>
    );
};

export default IncentiveCard;
